import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripDotsVertical } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';
import { useToggleAgentChat } from './useToggleAgentChat';

// Figma: 22x102 content + 8px top / 16px bottom padding.
const BADGE_HEIGHT = 126;
// Keep the badge clear of the viewport edges when dragging/clamping.
const EDGE_MARGIN = 8;
// Place the badge 100px above the bottom edge on first render.
const DEFAULT_BOTTOM_OFFSET = 100;
// Pointer travel (px) past which a press counts as a drag, not a click.
const DRAG_THRESHOLD = 3;
// antd Layout.Sider collapses over motionDurationMid (0.2s). A host sider's
// "gone" report lands when its collapse STARTS, so the badge waits this long
// before reappearing (see the suppression hold below).
const SIDER_SLIDE_MS = 200;

const BADGE_TOP_STORAGE_KEY = 'robin-agent-chat-badge-top';

const clampTop = (top: number): number => {
  const max = window.innerHeight - BADGE_HEIGHT - EDGE_MARGIN;
  return Math.min(Math.max(top, EDGE_MARGIN), max);
};

const readStoredTop = (): number | null => {
  try {
    const stored = localStorage.getItem(BADGE_TOP_STORAGE_KEY);
    if (stored === null) {
      return null;
    }
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredTop = (top: number): void => {
  try {
    localStorage.setItem(BADGE_TOP_STORAGE_KEY, String(top));
  } catch {
    // localStorage may be unavailable in private mode
  }
};

type AgentChatBadgeProps = {
  /** Whether the agent sider currently shows; the badge docks to its left edge. */
  siderVisible: boolean;
  /**
   * The agent sider's root element (antd Sider forwards its ref to it). The
   * badge glues itself to this element's live left edge — see the layout
   * effect below.
   */
  siderRef: React.RefObject<HTMLDivElement>;
  /**
   * Hide the badge because a host sider has its own entry point into the agent.
   */
  suppressed?: boolean;
};

/**
 * Floating entry point for the Robin Assistant: a gradient tab pinned to the
 * right edge of the viewport. Clicking it opens the panel (same action the old
 * nav menu item had — see useToggleAgentChat); dragging it vertically
 * repositions it, persisted per browser.
 *
 * Hidden while the panel shows — the panel's own header close button is the
 * way back. Also hidden while `suppressed` (see the prop). It stays MOUNTED
 * while hidden (drag state and the ResizeObserver survive), and reappears only
 * once a close has fully landed: `siderVisible` flips false at the START of
 * the collapse, so the badge also waits for the sider's rendered width to
 * reach 0 (see the dock effect) rather than popping in beside the
 * still-closing panel.
 */
export const AgentChatBadge = ({
  siderVisible,
  siderRef,
  suppressed = false,
}: AgentChatBadgeProps): JSX.Element => {
  const { t } = useTranslation('globalNavigation');
  const toggleAgentChat = useToggleAgentChat();

  // Clamped: a position persisted on a taller window would otherwise render
  // the badge below the fold with no way to recover short of a resize — and
  // this badge is the assistant's only entry point.
  const [top, setTop] = useState<number>(() =>
    clampTop(
      readStoredTop() ??
        window.innerHeight - BADGE_HEIGHT - DEFAULT_BOTTOM_OFFSET
    )
  );
  const [dragging, setDragging] = useState(false);

  // Pointer bookkeeping for the active drag. `moved` doubles as the
  // click-suppressor: a drag's pointerup still fires a click on the button,
  // which must not toggle the panel.
  const dragStateRef = useRef<{
    pointerId: number;
    startY: number;
    startTop: number;
  } | null>(null);
  const movedRef = useRef(false);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      // Primary button / single touch only.
      if (event.button !== 0) {
        return;
      }
      dragStateRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startTop: top,
      };
      movedRef.current = false;
      // Not implemented in jsdom; optional-call keeps tests running.
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [top]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragStateRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }
      const delta = event.clientY - drag.startY;
      if (!movedRef.current && Math.abs(delta) < DRAG_THRESHOLD) {
        return;
      }
      movedRef.current = true;
      setDragging(true);
      setTop(clampTop(drag.startTop + delta));
    },
    []
  );

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragStateRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }
      dragStateRef.current = null;
      setDragging(false);
      if (movedRef.current) {
        setTop((current) => {
          writeStoredTop(current);
          return current;
        });
      }
    },
    []
  );

  const cancelDrag = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      endDrag(event);
      // No click follows a pointercancel (browser stole the gesture, tab
      // switch), so the suppressor would stay armed and swallow the next
      // keyboard activation — Enter/Space fire a click with no pointerdown
      // to clear it.
      movedRef.current = false;
    },
    [endDrag]
  );

  const onClick = useCallback(() => {
    // A drag's trailing click must not toggle the panel.
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    toggleAgentChat();
  }, [toggleAgentChat]);

  // Keep the badge on-screen when the viewport shrinks under it.
  useEffect(() => {
    const onResize = () => setTop((current) => clampTop(current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Whether any of the sider is still on screen. `siderVisible` alone can't
  // hide the badge through a close animation — it flips false the moment the
  // close STARTS — so the badge also stays hidden until the sider's rendered
  // width lands back at 0 (fed by the dock observer below).
  const [siderHasWidth, setSiderHasWidth] = useState(false);

  // Track the sider's LIVE left edge: a ResizeObserver reports the sider's
  // width every rendered frame, so however the sider moves — antd's slide, an
  // instant handoff swap, or anything in between — the badge's `right` stays
  // docked to it and, above, the badge knows the exact frame a close fully
  // lands. The `right` write is imperative (not state) so a 0.2s slide
  // doesn't churn ~12 React renders; the visibility bit IS state, but only
  // changes at the 0-width boundary (an unchanged setState bails out). A
  // PASSIVE effect, not a layout effect: the badge renders before its sibling
  // sider, and on a shared mount commit (page restored with the panel open) a
  // layout effect would run before the sider's host ref attaches, bail, and
  // never re-run — leaving the badge undocked under the open panel. Passive
  // effects run after the whole commit's refs are attached.
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const sider = siderRef.current;
    const button = buttonRef.current;
    if (!sider || !button) {
      return;
    }
    const dock = () => {
      const width = sider.getBoundingClientRect().width;
      button.style.right = `${width}px`;
      setSiderHasWidth(width > 0);
    };
    dock();
    // Not implemented in jsdom; the initial dock above still runs (and re-runs
    // on visibility flips via the dependency below).
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(dock);
    observer.observe(sider);
    return () => observer.disconnect();
    // `siderVisible` re-runs the initial dock as a fallback when there is no
    // ResizeObserver; with one, the extra run is a harmless duplicate.
  }, [siderRef, siderVisible]);

  // `suppressed` drops the moment the host sider STARTS closing — its report
  // is state-driven, and unlike the dock effect above the shell can't observe
  // another app's sider width — so hold the hide for the length of antd's
  // slide before letting the badge back in; otherwise it pops in beside the
  // still-closing panel. Re-suppression cancels a pending release.
  const [suppressionHeld, setSuppressionHeld] = useState(suppressed);
  useEffect(() => {
    if (suppressed) {
      setSuppressionHeld(true);
      return;
    }
    const timer = window.setTimeout(
      () => setSuppressionHeld(false),
      SIDER_SLIDE_MS
    );
    return () => window.clearTimeout(timer);
  }, [suppressed]);

  return (
    <BadgeButton
      ref={buttonRef}
      type="button"
      aria-expanded={siderVisible}
      // display:none (not conditional render) keeps the component mounted
      // while hidden — see the component doc. Hidden while the panel shows, any
      // of it is still on screen mid-close, OR a host sider's own assistant
      // entry point is showing (held through that sider's close). Inline so it always wins over the class rule's
      // own `display`, and drops out cleanly when visible.
      style={{
        display:
          siderVisible || siderHasWidth || suppressionHeld ? 'none' : undefined,
      }}
      $top={top}
      $dragging={dragging}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={cancelDrag}
      onClick={onClick}
    >
      <GripIcon icon={faGripDotsVertical} aria-hidden />
      <BadgeLabel>{t('agentChatTitle', 'Robin Agent')}</BadgeLabel>
    </BadgeButton>
  );
};

// Figma "Floating Button" badge (node 4169-17669): left-rounded vertical tab
// flush with the right edge, gradientPrimaryActive fill, boxShadowSecondary.
const BadgeButton = styled.button<{
  $top: number;
  $dragging: boolean;
}>`
  all: unset;
  box-sizing: border-box;
  position: fixed;
  top: ${({ $top }) => $top}px;
  /* Horizontal position is written imperatively — the badge docks to the
     sider's live left edge (see the ResizeObserver layout effect). */
  right: 0;
  z-index: 100;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ant-margin-xs, 8px);
  padding: var(--ant-margin-xs, 8px) var(--ant-margin-xs, 8px)
    var(--ant-margin, 16px);
  border-radius: 12px 0 0 12px;
  background: var(
    --gradientPrimaryActive,
    linear-gradient(
      86deg,
      var(--ant-color-primary-active, #730749) 0%,
      var(--ant-color-link-active, #17559c) 100%
    )
  );
  box-shadow: var(
    --ant-box-shadow-secondary,
    0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05)
  );
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'pointer')};
  /* The badge owns vertical pans; without this, touch dragging scrolls the page. */
  touch-action: none;
  user-select: none;

  &:hover {
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid var(--ant-color-primary, #be1f77);
    outline-offset: 2px;
  }
`;

const GripIcon = styled(FontAwesomeIcon)`
  font-size: 14px;
  color: #ffffff;
  /* Figma places the vertical-grip glyph rotated into a horizontal grip. */
  transform: rotate(90deg);
`;

// Figma Text Base/Strong, drawn top-to-bottom (rotate(90deg) in the design;
// vertical-rl renders Latin text identically without manual box math).
const BadgeLabel = styled.span`
  writing-mode: vertical-rl;
  white-space: nowrap;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.175px;
`;
